import React, { memo } from 'react'
import AddIcon from 'transfix-icons/build/AddIcon'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import ShieldFilledIcon from 'transfix-icons/build/ShieldFilledIcon'
import StarFilledIcon from 'transfix-icons/build/StarFilledIcon'
import WarningFilledIcon from 'transfix-icons/build/WarningFilledIcon'
import Card, { ICardProps } from 'transfix-ui/components/Card'
import { Divider, Link } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { ICarrierApplicationStates, Maybe } from 'types/graphqlTypes'
import { getNewCarrierStatus } from 'utils/carriers/getNewCarrierStatus'
import { useFeatureFlag, useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import ExtendedCarrierScorecard from 'apps/carrier/components/GeneralInformation/CarrierSummary/components/ExtendedCarrierScorecard'
import { useCarrierExtendedInformation } from 'apps/carrier/components/GeneralInformation/CarrierSummary/components/ExtendedCarrierScorecard/hooks/useCarrierExtendedInformation'
import CarrierRequirementsStatus from './CarrierRequirementsStatus/CarrierRequirementsStatus'
import IconWithStatus from './IconWithStatus'
import { ICarrierRequirementsQuery } from './graphql/CarrierRequirements'
import { useCarrierStatusCardDataQuery } from './graphql/CarrierStatusCardData'
import { getCarrierStateLabel } from './utils'

interface ICarrierInfoNumberProps {
  title: string
  value: string | null
}

export const CarrierInfoNumber = memo<ICarrierInfoNumberProps>(({ title, value }) => {
  return (
    <div>
      <Typography variant='button'>{title}:</Typography>
      <Box display='inline-block' ml={0.5}>
        <Typography variant='caption' color='textSecondary'>
          {value || 'N/A'}
        </Typography>
      </Box>
    </div>
  )
})

export interface ICarrierStatusCardProps extends Omit<ICardProps, 'title' | 'children'> {
  carrierApplicationId?: Maybe<string>
  shipmentId: string
  onCarrierRequirementsLoaded?: (data: ICarrierRequirementsQuery) => void
}

const CarrierStatusCard = memo<ICarrierStatusCardProps>(
  ({ carrierApplicationId, onCarrierRequirementsLoaded, shipmentId }) => {
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isExtendedCarrierScorecardEnabled } = getFlag('extendedScorecard')
    const isUseNewStateTagsMpEnabled = useFeatureFlag('useNewStateTagsMp')

    const { data } = useCarrierStatusCardDataQuery({
      variables: { carrierApplicationId: carrierApplicationId || '' },
      skip: !carrierApplicationId,
    })
    const { carrierApplication } = data || {}
    const { dotNumber, fmcsaRating, isTrusted, mcNumber, name: carrierName, state } =
      carrierApplication || {}
    const { data: extendedCarrierData } = useCarrierExtendedInformation(carrierApplicationId || '')

    const statusText = getNewCarrierStatus(
      getCarrierStateLabel(state) || '',
      isUseNewStateTagsMpEnabled
    )
    const isFresh = state === ICarrierApplicationStates.new
    const isActive = state === ICarrierApplicationStates.activated

    return (
      <Card title={carrierApplicationId ? null : 'Carrier'}>
        {isExtendedCarrierScorecardEnabled ? (
          <div>
            {!carrierApplicationId && (
              <Typography variant='caption' color='textSecondary'>
                Carrier will appear when you&apos;ve chosen a carrier.
              </Typography>
            )}
            {carrierApplicationId && (
              <>
                <ExtendedCarrierScorecard
                  className='blueTitle'
                  carrierId={carrierApplicationId}
                  carrierLinkPosition='top'
                  {...extendedCarrierData}
                />
                <Divider />
                {carrierApplicationId && (
                  <CarrierRequirementsStatus
                    carrierId={carrierApplicationId}
                    shipmentId={shipmentId}
                    onCarrierRequirementsLoaded={onCarrierRequirementsLoaded}
                  />
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            {!carrierApplicationId && (
              <Typography variant='caption' color='textSecondary'>
                Carrier will appear when you&apos;ve chosen a carrier.
              </Typography>
            )}
            {carrierApplicationId && (
              <Box display='flex' alignItems='center'>
                <Typography variant='body1' color='primary'>
                  <Link
                    href={`${__ROOT__}/carriers/${carrierApplicationId}`}
                    target='_blank'
                    rel='no-referrer'>
                    {carrierName}
                  </Link>
                </Typography>
                {isTrusted && (
                  <Box ml={1}>
                    <StarFilledIcon size='large' color='centerline' />
                  </Box>
                )}
              </Box>
            )}
            <Box display='flex' alignItems='center'>
              <CarrierInfoNumber title='MC' value={mcNumber || null} />
              <Box mx={0.5}>&middot;</Box>
              <CarrierInfoNumber title='DOT' value={dotNumber || null} />
            </Box>
            {carrierApplicationId && (
              <div>
                <Box display='flex' alignItems='center' mt={3} pb={3}>
                  {statusText && (
                    <IconWithStatus
                      icon={isFresh ? WarningFilledIcon : CheckFilledIcon}
                      text={statusText}
                      color={isActive ? 'info' : 'warning'}
                    />
                  )}
                  {!isFresh && (
                    <>
                      <Box display='flex' alignItems='center' ml={2}>
                        <IconWithStatus icon={ShieldFilledIcon} text='Satisfactory' color='info' />
                      </Box>
                      {fmcsaRating && (
                        <Box ml={1}>
                          <Typography variant='body1' color='textSecondary'>
                            {fmcsaRating}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
                <Divider />
                {carrierApplicationId && (
                  <CarrierRequirementsStatus
                    carrierId={carrierApplicationId}
                    shipmentId={shipmentId}
                    onCarrierRequirementsLoaded={onCarrierRequirementsLoaded}
                  />
                )}
              </div>
            )}
          </div>
        )}
        {!carrierApplicationId && (
          <Box mt={2}>
            <Link href={`${__ROOT__}/carriers/new`} target='_blank' rel='no-referrer'>
              <Box display='flex' alignItems='center'>
                <Box display='flex' alignItems='center' mr={0.5}>
                  <AddIcon />
                </Box>
                <div>Onboard new carrier</div>
              </Box>
            </Link>
          </Box>
        )}
      </Card>
    )
  },
  (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps)
)

export default CarrierStatusCard
